import { ApiError } from '@/lib/apiClient';
import { ERROR_MESSAGES, STATUS_MESSAGES, friendlyFieldMessage } from './errorCatalog';

// Patterns that indicate a technical/internal message we must never show to end users.
const TECHNICAL = [
  /request failed with status/i,
  /status code\s*\d+/i,
  /\bexception\b/i,
  /stack\s*trace/i,
  /traceid/i,
  /null reference/i,
  /^[A-Z][A-Z0-9_]{2,}$/, // ALL_CAPS_ERROR_CODE
  /System\.[A-Za-z]/,
  /\bat\s+[A-Za-z][\w.]+\(/, // stack frame
  /cannot read propert/i,
  /undefined is not/i,
  /network error/i,
  // Database / SQL leakage guards.
  /duplicate key value/i,
  /violates .*constraint/i,
  /\bSQLSTATE\b/i,
  /\bnpgsql\b/i,
  /ORA-\d{3,}/,
  /\btimeout\b.*\b(stream|reading|connection)\b/i,
];

/** True when a message is safe, human-readable business text (not a code/stack/technical string). */
function isFriendly(msg?: string | null): boolean {
  if (!msg) return false;
  const m = msg.trim();
  if (!m) return false;
  return !TECHNICAL.some((re) => re.test(m));
}

/**
 * Guard for toast copy: returns app-authored text as-is, but replaces any string that looks
 * technical (a leaked backend/exception/SQL message) with a safe, friendly fallback. Ensures a
 * success/info notification can never surface raw backend text.
 */
export function toFriendlyMessage(message: string, fallback = 'Done.'): string {
  return isFriendly(message) ? message : fallback;
}

/**
 * All server-provided validation messages for this error, normalized to friendly copy (empty if
 * none). Prefers the field-keyed map; falls back to the flat list.
 */
export function getServerValidationMessages(err: unknown): string[] {
  if (!(err instanceof ApiError)) return [];
  if (err.fieldErrors && Object.keys(err.fieldErrors).length) {
    const all = Object.entries(err.fieldErrors).flatMap(([key, msgs]) => (msgs ?? []).map((m) => friendlyFieldMessage(m, key)));
    return Array.from(new Set(all));
  }
  return err.errors?.length ? Array.from(new Set(err.errors.map((m) => friendlyFieldMessage(m)))) : [];
}

/**
 * Converts any error into calm, business-friendly text. Never exposes API/controller/DTO names,
 * status numbers, error codes, trace IDs, or stack traces.
 *
 * Validation (400/422) rule: ALWAYS prefer the server's `errors[]` and show every message
 * verbatim (joined by newlines) — never replace them with a generic "Validation failed".
 */
export function humanizeError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof ApiError) {
    // 1. Field validation → friendly, per-field messages (deterministic map preferred).
    const fieldMsgs = getServerValidationMessages(err);
    if (fieldMsgs.length) return fieldMsgs.join('\n');

    // 2. Known error code (incl. NETWORK_ERROR/TIMEOUT) → curated friendly message.
    if (err.errorCode && ERROR_MESSAGES[err.errorCode]) return ERROR_MESSAGES[err.errorCode];

    // 3. A friendly, specific backend message (no technical leak) → show it as-is.
    if (isFriendly(err.message)) return err.message!;

    // 4. Friendly fallback by HTTP status.
    return STATUS_MESSAGES[err.status] ?? fallback;
  }

  if (err instanceof Error && isFriendly(err.message)) return err.message;
  return fallback;
}

/**
 * Maps each server validation message to a form field (via react-hook-form `setError`) so it
 * shows beneath that field. Messages that can't be matched to a field are returned so the caller
 * can surface them (e.g. a toast). Matching is by the field name quoted in the message
 * (FluentValidation emits e.g. "The length of 'Iso Code' …" → field `isoCode`).
 */
export function applyServerValidation(
  err: unknown,
  // Accepts react-hook-form's setError (its field-path param is a subtype of string).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setError: (name: any, error: { type: string; message: string }) => void,
  fieldNames: string[],
): string[] {
  if (!(err instanceof ApiError)) return [];

  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const byNorm = new Map(fieldNames.map((f) => [norm(f), f]));
  const unmatched: string[] = [];

  // 1. Preferred path: the server's field-keyed map (deterministic). Keys are PascalCase
  //    property names; normalization matches them to camelCase form fields.
  if (err.fieldErrors && Object.keys(err.fieldErrors).length) {
    for (const [key, msgs] of Object.entries(err.fieldErrors)) {
      const field = byNorm.get(norm(key));
      const message = friendlyFieldMessage(msgs?.[0], key);
      if (field) setError(field, { type: 'server', message });
      else unmatched.push(message);
    }
    return unmatched;
  }

  // 2. Fallback: flat messages — match by the field display name quoted in the text
  //    (FluentValidation emits e.g. "The length of 'Iso Code' …" → field `isoCode`).
  if (!err.errors?.length) return [];
  for (const msg of err.errors) {
    const quoted = msg.match(/['‘’"“”]([^'‘’"“”]+)['‘’"“”]/)?.[1];
    const field = quoted ? byNorm.get(norm(quoted)) : undefined;
    const message = friendlyFieldMessage(msg);
    if (field) setError(field, { type: 'server', message });
    else unmatched.push(message);
  }
  return unmatched;
}
